import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryApi } from '@/lib/api/inventory';
import { formatToEST } from '@/lib/utils/date-format';
import type { InventoryItem, Location } from '@/types/inventory';

interface LocationDetailModalProps {
  location: Location | null;
  onClose: () => void;
}

export function LocationDetailModal({ location, onClose }: LocationDetailModalProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadItems = useCallback(async () => {
    if (!location) return;

    try {
      setIsLoading(true);
      const data = await inventoryApi.getInventory({
        locationId: location.id,
        pageSize: 1000,
      });
      setItems(data?.items ?? []);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setIsLoading(false);
    }
  }, [location]);

  useEffect(() => {
    if (location) {
      loadItems();
    }
  }, [location, loadItems]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-900 text-green-200';
      case 'Assigned':
        return 'bg-yellow-900 text-yellow-200';
      case 'Picked':
        return 'bg-blue-900 text-blue-200';
      case 'Relocated':
        return 'bg-amber-900 text-amber-200';
      case 'Removed':
        return 'bg-red-900 text-red-200';
      default:
        return 'bg-gray-700 text-gray-400';
    }
  };

  if (!location) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <div className="relative bg-gray-800 rounded-lg border border-gray-700 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Location: {location.code}</h2>
              <p className="text-sm text-gray-400">{location.description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading items...</p>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-400">
                  {items.length} item{items.length !== 1 ? 's' : ''} in this location
                </p>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No items in this location
                </div>
              ) : (
                <div className="bg-gray-750 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Barcode
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Manifest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Sort ID 1
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Sort ID 2
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                          Received
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-800">
                          <td className="px-4 py-3 font-mono">
                            <button
                              onClick={() => {
                                onClose();
                                navigate(`/inventory/${item.id}`);
                              }}
                              className="text-blue-400 hover:text-blue-300 hover:underline"
                            >
                              {item.barcodeValue}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm">{item.manifestBargeName}</td>
                          <td className="px-4 py-3">{item.sortId1 || '-'}</td>
                          <td className="px-4 py-3">{item.sortId2 || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {formatToEST(item.receivedTimestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
}
